
import { userIdentifierAtom } from "@plura/client/hooks";
import { trpc } from "@plura/shared/trpc";
import { Conversation, LeadForm } from "@plura/shared/types";
import { cn } from "@plura/shared/utils";
import * as Sentry from '@sentry/nextjs';
import { motion } from "framer-motion";
import { useAtom } from "jotai";
import { toast } from "sonner";
import Button from '../../../../atoms/Button';
import Loader from '../../../../atoms/Loader';
import "./continue-screen.css";

interface Props {
  leadForm: LeadForm
  setCurrentStepId: (id: string) => void
  resetForm: (conversationId?: string) => void
  previousConversation?: Conversation | null
}

const ContinueScreen = ({ leadForm, setCurrentStepId, resetForm, previousConversation }: Props) => {
  const [userIdentifier, _] = useAtom(userIdentifierAtom)
  const deleteConversationForContact = trpc.contact.deleteConversationForContact.useMutation()

  return (
    <div className="screenRenderer">
      <motion.div
        key="continueScreen-title"
        exit={{ opacity: 0, transform: 'translateY(-30px)' }}
        initial={{ opacity: 0, transform: 'translateY(30px)' }}
        animate={{ opacity: 1, transform: 'translateY(0)' }}
      >
        <h1>Submission in progress...</h1>
      </motion.div>
      <motion.div
        key="continueScreen-caption"
        exit={{ opacity: 0, transform: 'translateY(-30px)' }}
        initial={{ opacity: 0, transform: 'translateY(30px)' }}
        animate={{ opacity: 1, transform: 'translateY(0)' }}
        transition={{ delay: 0.1 }}
      >
        <h2>You seem to have an ongoing submission. Would you like to pick up where you left off?</h2>
      </motion.div>

      <div className={cn('continueButtonWrapper')}>
        {
          leadForm &&
          <Button isBlack onClick={() => {
            const currentStep = previousConversation?.conversationItem
              ?.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
              .find(item => item.role === 'Assistant')?.journeyItemId
            const currentBlockId = leadForm.journeyItems?.find(item => item.id === currentStep)?.blockId
            resetForm(previousConversation?.id)

            if (currentBlockId) {
              setCurrentStepId(currentBlockId)
            } else if (leadForm.journeyItems?.[0].blockId) {

              setCurrentStepId(leadForm.journeyItems[0].blockId)
            } else {
              toast.error('There seems to be an issue, please refresh and try again', {
                description: 'If the problem persists, please contact support.'
              })

              Sentry.captureException(new Error('No journey items found when resuming conversation'))
            }
          }}>
            Yes, continue
          </Button>
        }
        <Button isStroke onClick={() => {
          if (!userIdentifier[leadForm.workspaceId]) {
            if (leadForm.journeyItems?.[0].blockId) setCurrentStepId(leadForm.journeyItems[0].blockId)
            return
          }

          deleteConversationForContact.mutateAsync({
            contactId: userIdentifier[leadForm.workspaceId],
            collectionId: leadForm.collectionId
          }, {
            onSuccess: async () => {
              if (leadForm.journeyItems?.[0].blockId) {
                resetForm()
                setCurrentStepId(leadForm.journeyItems[0].blockId)
              } else {
                toast.error('There seems to be an issue, please refresh and try again', {
                  description: 'If the problem persists, please contact support.'
                })

                Sentry.captureException(new Error('No journey items found when restarting conversation'))
              }
            },
            onError: (error) => {
              toast.error('There seems to be an issue, please refresh and try again', {
                description: 'If the problem persists, please contact support.'
              })

              Sentry.captureException(error)
            }
          })
        }}
          processing={deleteConversationForContact.isPending}
        >
          No, start over
        </Button>
      </div>
    </div>
  )
}

export default ContinueScreen